/**
 * Pure logic for the first-run setup screen (`first_run_setup_screen.tsx`,
 * issue #23952) — display-only projections, kept out of the JSX so they are
 * unit-testable in `bun:test` without React or a DOM. Same "component holds
 * JSX + wiring, logic file holds transforms" split
 * `docs/testing-titanium-standard.md` requires for any component beyond
 * ~150 lines.
 *
 * Everything here is a pure display projection: what to show, in what order,
 * for the "From Ledger" card and the 4-step stepper. No mutation, no fetch,
 * no i18n resolution (the screen owns those).
 */

/** ISO-3166 alpha-2 country code — matches `entities.country` on the wire. */
export type CountryCode = string;

/**
 * The subset of `entity.clientGet`'s `entity` row the "From Ledger" card
 * cares about. Deliberately narrow — the screen never invents or edits any
 * of these; it renders whichever fields exist and hides the rest.
 */
export interface EntityDetails {
  name: string;
  country: string | null;
  currency_code: string;
  financial_year_end: number;
  registration_no: string | null;
  address: EntityAddressJson | null;
}

/** Shape of the JSON blob `entities.address` carries (see `apps/api/src/db/schema/control_plane/clients.ts`'s `Address`). */
export interface EntityAddressJson {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  attention?: string;
}

/**
 * One field to render inside the "From Ledger" card. The screen maps each
 * item onto a `<dt>`/`<dd>` pair; the "hide the field when there is no
 * value" rule is enforced HERE so the JSX is a straight map with no
 * conditionals — that is, absent fields never render at all, so an empty
 * entity row (a fresh Ledger create) does not paint a series of "—" holes.
 */
export interface EntityDetailsField {
  /** Stable id so React keys are meaningful and tests can assert order. */
  id:
    | 'legal_name'
    | 'country'
    | 'currency'
    | 'financial_year_end'
    | 'registration_number'
    | 'address';
  /**
   * The already-translated label ready for a `<dt>`. The name is `label`,
   * not `labelKey`, because it holds the resolved copy the caller passed
   * in — never a raw i18n key. The old name was a misnomer flagged on the
   * adversarial review of PR #23958 (a caller that renders `field.labelKey`
   * would show `field.labelKey` verbatim if it happened to hold a real i18n
   * key, defeating the labels-object indirection).
   */
  label: string;
  /** The already-formatted value string, ready for a `<dd>`. */
  value: string;
}

/**
 * The fiscal year END month is stored 1-12 (see
 * `wagepro_store_settings.ts`'s docblock for the semantic-inversion note).
 * This resolves a MONTH-END display like "March 2026 year-end" without
 * committing to a year — the label just names the month. `format_month_name`
 * in `lib/format.ts` is already used across the app for this exact purpose;
 * we take it as an injected formatter here to keep this file free of the
 * app's format helpers so it stays bun:test-pure.
 */
export type MonthFormatter = (month: number) => string;

/**
 * Turn an ISO-3166 alpha-2 code (`entities.country` — 'LK', 'GB', 'ID') into
 * a localized country name for display. Injected so this file remains
 * bun:test-pure — the caller supplies a real `Intl.DisplayNames`-backed
 * formatter (see the setup screen).
 *
 * Returns the input unchanged when the formatter cannot resolve a name —
 * defensive against a malformed/empty code, so the field renders the raw
 * value instead of a blank cell (never crashes). This is the fix for the
 * "country renders raw 'LK'" LOW finding on the adversarial review of
 * PR #23958.
 */
export type CountryFormatter = (isoCode: string) => string;

/**
 * Pure formatter — a single-line, comma-separated postal address in the
 * order line1, line2, city, state, postal_code, country. Every field is
 * optional; the result is only the fields that ARE set, joined with `', '`.
 * Returns `null` (not `''`) when nothing renders, so the caller can treat
 * "no address" identically to "no country" — every "hide when empty" branch
 * is one predicate: `value === null`.
 */
export function formatEntityAddress(
  address: EntityAddressJson | null | undefined,
): string | null {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ].filter(
    (part): part is string => typeof part === 'string' && part.trim() !== '',
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/**
 * Project the entity into the ordered list of fields the "From Ledger" card
 * renders. The order is the same as the docstring on `EntityDetailsField.id`:
 * legal name, country, currency, fy-end month, registration number, address.
 * Absent fields (empty strings, nulls) are omitted — never rendered as a
 * placeholder. The financial-year-end month is always present (backend
 * default 12) so it always renders.
 */
export function projectEntityDetails(
  entity: EntityDetails,
  formatMonthName: MonthFormatter,
  formatCountry: CountryFormatter,
  labels: {
    legal_name: string;
    country: string;
    currency: string;
    financial_year_end: string;
    registration_number: string;
    address: string;
  },
): readonly EntityDetailsField[] {
  const fields: EntityDetailsField[] = [];

  if (entity.name.trim() !== '') {
    fields.push({
      id: 'legal_name',
      label: labels.legal_name,
      value: entity.name,
    });
  }

  if (entity.country !== null && entity.country.trim() !== '') {
    fields.push({
      id: 'country',
      label: labels.country,
      value: formatCountry(entity.country),
    });
  }

  fields.push({
    id: 'currency',
    label: labels.currency,
    value: entity.currency_code,
  });

  fields.push({
    id: 'financial_year_end',
    label: labels.financial_year_end,
    value: formatMonthName(entity.financial_year_end),
  });

  if (entity.registration_no !== null && entity.registration_no.trim() !== '') {
    fields.push({
      id: 'registration_number',
      label: labels.registration_number,
      value: entity.registration_no,
    });
  }

  const address = formatEntityAddress(entity.address);
  if (address !== null) {
    fields.push({
      id: 'address',
      label: labels.address,
      value: address,
    });
  }

  return fields;
}

/**
 * One entry in the 4-step stepper. Every non-current step renders as
 * `coming_next` today — no link targets are surfaced from this screen (the
 * former `'link'` variant + `href` field were dropped in the second
 * adversarial-review pass on PR #23958: `resolveSetupSteps` below only
 * ever produced `'current'` / `'coming_next'`, and the JSX branch behind
 * `href !== null` was consequently dead code).
 */
export interface SetupStep {
  id: 'confirm_details' | 'pay_schedule' | 'statutory' | 'first_employee';
  status: 'current' | 'coming_next';
}

/**
 * The four setup steps in fixed order. `confirm_details` is always the
 * current step on this screen (that IS the screen). All THREE later steps
 * render as `coming_next` (disabled) here.
 *
 * The pre-fixup code linked `pay_schedule` to `#/settings/payroll` as a
 * live link. That was broken twice: (a) BEFORE connect, `#/settings/payroll`
 * requires a resolved `wagepro_companies` row and lands on an empty/broken
 * settings surface; (b) AFTER connect, the URL hash was left pointing at
 * `#/settings/payroll` when this screen unmounted, so the ConnectLandingGate
 * dropped the user onto Settings > Payroll instead of the dashboard. Both
 * were MEDIUM findings on the adversarial review of PR #23958. Rendering
 * every later step as `coming_next` here means the "Set up payroll" primary
 * action is the ONLY forward path, matching the `connect_landing_logic.ts`
 * rules: once the org is connected the setup screen unmounts entirely and
 * the dashboard takes over.
 */
export function resolveSetupSteps(): readonly SetupStep[] {
  return [
    { id: 'confirm_details', status: 'current' },
    { id: 'pay_schedule', status: 'coming_next' },
    { id: 'statutory', status: 'coming_next' },
    { id: 'first_employee', status: 'coming_next' },
  ];
}
